/**
 * Helper functions and badge components for PODetailModal.
 */

import type { PODocument, POChangeLog } from '../../lib/api';
import type { TimelineItem } from './timeline/VerticalTimeline';
import { changeFieldLabel } from './changelogFields';
import { type LineStatusFields } from './lineStatus';
import { getLineStatusBadge as renderLineStatusBadge } from './tabs/po-lines/utils';
import { poHeaderStatusClass } from './poHeaderStatus';

export const getStatusBadgeClass = (status: string) => {
  // CR-7412: one owner — see poHeaderStatus.ts for why four copies of this was the
  // defect rather than the tidiness problem it looks like.
  return poHeaderStatusClass(status);
};

/**
 * The line's status badge on the PO header's Lines tab.
 *
 * EXPEDITING-PARITY PR 2 (Aaron, 17 Sep 2026): this had its OWN copy of the
 * status map, and it disagreed with the grid's on almost every row — "Change
 * Proposed (S)" where the grid said "Changed", "Accepted" where the grid said
 * "WIP", different colours throughout, and no idea the line could be on hold.
 * One line, two screens, two answers. It asks `statusOf` now, like everything
 * else, and takes the LINE because the hold is part of the status.
 *
 * @param line - The PO line.
 * @returns The status badge element, or null when there is nothing to show.
 */
export const getLineStatusBadge = (line?: LineStatusFields | null) => {
  // The BADGE is the grid's (one owner, one set of classes); what is local here is
  // the decision not to draw one at all for a line with no status and no hold. Two
  // copies of the markup was a second owner waiting to drift (type lens, round 2).
  if (!line || (!line.acknowledge_status && !line.on_hold)) return null;
  return renderLineStatusBadge(line);
};

/**
 * The RECEIPT state of a line — open / partial / received / cancelled. Not the
 * expediting status: that is `getLineStatusBadge` above, which is what a user
 * means by "status".
 *
 * Renamed 17 Sep 2026 (EXPEDITING-PARITY PR 2). It was ALSO called
 * `getLineStatusBadge`, so this file exported two functions whose names claimed
 * the same thing about the same object — the naming half of the very confusion
 * Aaron asked us to end. It has no callers today; kept rather than deleted
 * because the receipt state is real and a surface may yet want it.
 *
 * AND BEFORE ANY SURFACE ADOPTS IT (CR-7305, carried over on the merge): the map
 * below is WRONG for a PO line. `PurchaseOrderLine.STATUS_CHOICES` is pending /
 * partially_received / received / cancelled, so `open` can never match and
 * `pending` — the commonest value, and the default — has no entry and falls
 * through to the raw-string fallback. That grey lowercase "pending" pill is what
 * CR-7305 was raised for. Fix the keys first.
 */
export const getReceiptStatusBadge = (status: string) => {
  const statusConfig: Record<string, { label: string; className: string }> = {
    // CR-7412: a finished line reads CLOSED, which is legacy's word for this
    // bit (`NetworkSQL.cs:6886` renders POL.Completed as Open/Closed).
    // `partially_received` keeps "Partial" — a part-received line is not
    // closed, and Received stays the QUANTITY word throughout (Qty Received,
    // Last Received Date, the GRN and ASN labels are all untouched).
    open: { label: 'Open', className: 'bg-blue-100 text-blue-800' },
    partially_received: { label: 'Partial', className: 'bg-yellow-100 text-yellow-800' },
    received: { label: 'Closed', className: 'bg-slate-100 text-slate-800' },
    cancelled: { label: 'Cancelled', className: 'bg-slate-100 text-slate-800' },
  };

  const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
      {config.label}
    </span>
  );
};


export const formatFieldName = (field: string) => changeFieldLabel(field);

export const formatFileSize = (bytes?: number) => {
  if (!bytes) return 'Unknown';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

export const getDocumentTypeLabel = (type: string) => {
  const typeLabels: Record<string, string> = {
    po_pdf: 'Purchase Order',
    material_cert: 'Material Certificate',
    certificate: 'Certificate',
    drawing: 'Drawing',
    specification: 'Specification',
    test_report: 'Test Report',
    inspection_report: 'Inspection Report',
    other: 'Other',
  };
  return typeLabels[type] || type;
};

export const getDocumentTypeColor = (type: string) => {
  const colorMap: Record<string, string> = {
    po_pdf: 'bg-blue-100 text-blue-700',
    material_cert: 'bg-green-100 text-green-700',
    certificate: 'bg-purple-100 text-purple-700',
    drawing: 'bg-orange-100 text-orange-700',
    specification: 'bg-indigo-100 text-indigo-700',
    test_report: 'bg-yellow-100 text-yellow-700',
    inspection_report: 'bg-pink-100 text-pink-700',
    other: 'bg-gray-100 text-gray-700',
  };
  return colorMap[type] || 'bg-gray-100 text-gray-700';
};

export const groupDocumentsByType = (documents: PODocument[]) => {
  const grouped: Record<string, PODocument[]> = {};
  documents.forEach((doc) => {
    if (!grouped[doc.document_type]) {
      grouped[doc.document_type] = [];
    }
    grouped[doc.document_type].push(doc);
  });
  // Sort documents within each group by upload date (newest first)
  Object.keys(grouped).forEach((type) => {
    grouped[type].sort((a, b) => new Date(b.upload_date).getTime() - new Date(a.upload_date).getTime());
  });
  return grouped;
};

export const convertChangelogsToTimeline = (changelogs: POChangeLog[]): TimelineItem[] => {
  return changelogs.map((log) => ({
    id: log.id,
    timestamp: log.change_date,
    user: log.changed_by_details?.full_name || 'Unknown User',
    userRole: undefined,
    action: `Changed ${formatFieldName(log.field_changed)}`,
    field: formatFieldName(log.field_changed),
    oldValue: log.old_value || undefined,
    newValue: log.new_value || undefined,
    reason: log.reason || undefined,
    type: 'update' as const,
  }));
};
